import { MutableRefObject, useEffect, useRef, useState } from 'react';

import { isEqual, uniqueId } from 'lodash-es';
import PQueue from 'p-queue';
import Sockette from 'sockette';

import { useAppData } from 'src/app/data';
import { getWebSocketUrl } from 'src/components/pipelines/utils/env-base-url';
import { capturePipelinesExceptionWithTags } from 'src/components/pipelines/utils/sentry';

type Criteria = Record<string, string | number | null | undefined>;
type OnMessageRef = MutableRefObject<(message: any) => any>;

type Subscription = {
  event: string;
  criteria: Criteria;
  onMessageRefs: Record</* hookId */ string, OnMessageRef>;
  subscribed: boolean;
  userUuid?: string;
};

function createSubscriptionManager() {
  const subscriptions: Subscription[] = [];

  function isEmpty() {
    return !subscriptions.length;
  }

  function addOnMessageRef(
    hookId: string,
    event: string,
    criteria: Criteria,
    onMessageRef: OnMessageRef,
    userUuid?: string
  ) {
    const sub = subscriptions.find(
      s => s.event === event && isEqual(s.criteria, criteria)
    );
    if (sub === undefined) {
      // Create a new subscription object
      subscriptions.push({
        event: event.toLowerCase(),
        criteria,
        onMessageRefs: { [hookId]: onMessageRef },
        subscribed: false,
        userUuid,
      });
    } else {
      sub.onMessageRefs[hookId] = onMessageRef;
    }
  }

  function removeOnMessageRef(hookId: string) {
    const sub = subscriptions.find(
      ({ onMessageRefs }) => !!onMessageRefs[hookId]
    );
    if (sub) {
      delete sub.onMessageRefs[hookId];
    }
    return sub;
  }

  function removeSubscription(sub: Subscription) {
    subscriptions.splice(subscriptions.indexOf(sub), 1);
  }

  function onMessage(message: Record<string, any>) {
    const event = message?.event?.toLowerCase();
    if (event) {
      subscriptions
        .filter(
          sub =>
            sub.event === event &&
            // Note: there is an assumption that ws event includes all fields in the criteria object.
            // It works for all use cases so far. Remove this when the assumption is not true any more.
            Object.entries(sub.criteria).every(
              ([key, value]) => message?.[key] === value
            )
        )
        .forEach(sub =>
          Object.values(sub.onMessageRefs).forEach(onMessageRef =>
            onMessageRef.current(message)
          )
        );
    }
  }

  function* getSubscriptionsNeedToBeSubscribed() {
    for (const sub of subscriptions.filter(s => !s.subscribed)) {
      yield sub;
      sub.subscribed = true;
    }
  }

  function resetSubscriptions() {
    subscriptions.forEach(sub => {
      sub.subscribed = false;
    });
  }

  return {
    addOnMessageRef,
    getSubscriptionsNeedToBeSubscribed,
    isEmpty,
    onMessage,
    removeOnMessageRef,
    removeSubscription,
    resetSubscriptions,
  };
}

type SubscriptionManager = ReturnType<typeof createSubscriptionManager>;

function createWebsocketManager(subMgr: SubscriptionManager, wsUrl?: string) {
  // Constraint only one `get()` or `close()` can be executed at the same time.
  const queue = new PQueue({ concurrency: 1 });

  // The Sockette instance. Should only be accessible via `get()`.
  // eslint-disable-next-line no-underscore-dangle
  let _ws: Sockette | undefined;
  let resolveClose: (() => void) | undefined = undefined;

  // For debugging purpose: 0 = redux based websocket; 1 = hook based websocket
  // We're actually not using this ID in the frontend
  const requestId = 1;

  function get() {
    return queue.add(async () => {
      if (_ws === undefined) {
        // Atomicity is ensured by p-queue
        // eslint-disable-next-line require-atomic-updates
        _ws = await open();
      }

      return _ws;
    });
  }

  function close() {
    return queue.add(async () => {
      if (_ws === undefined) {
        return;
      }

      // Continue this in ws.onclose()
      await new Promise<void>(resolve => {
        resolveClose = resolve;
        _ws?.close();
      });
      resolveClose = undefined;

      // Atomicity is ensured by p-queue
      // eslint-disable-next-line require-atomic-updates
      _ws = undefined;
    });
  }

  function open() {
    return new Promise<Sockette>(resolveOpen => {
      const ws = new Sockette(wsUrl ?? getWebSocketUrl(), {
        onopen: () => {
          resolveOpen(ws);

          // This is called in async
          sendSubscribeMessages();
        },

        onmessage: (ev: MessageEvent<string>) => {
          try {
            const message = JSON.parse(ev.data);
            if (message?.type === 'EVENT') {
              subMgr.onMessage(message);
            }
          } catch (e) {
            capturePipelinesExceptionWithTags(e, {
              segment: 'pipelines_websocket_onmessage',
            });
          }
        },

        onerror: () => {
          capturePipelinesExceptionWithTags(
            new Error('Pipelines Sockette onerror'),
            { segment: 'pipelines_websocket_onerror' }
          );
        },

        onclose: () => {
          subMgr.resetSubscriptions();

          // Continue in close()
          resolveClose?.();
        },
      });
    });
  }

  async function sendSubscribeMessages() {
    const ws = await get();
    for (const sub of subMgr.getSubscriptionsNeedToBeSubscribed()) {
      const { event, criteria, userUuid } = sub;
      ws.json({ type: 'SUBSCRIBE', event, criteria, userUuid, requestId });
    }
  }

  function subscribe(
    hookId: string,
    event: string,
    criteria: Criteria,
    onMessageRef: OnMessageRef,
    userUuid?: string
  ) {
    subMgr.addOnMessageRef(hookId, event, criteria, onMessageRef, userUuid);
    sendSubscribeMessages();
  }

  async function unsubscribe(hookId: string) {
    const sub = subMgr.removeOnMessageRef(hookId);
    if (sub) {
      const ws = await get();
      const { event, criteria, userUuid } = sub;
      ws.json({ type: 'UNSUBSCRIBE', event, criteria, userUuid, requestId });
      subMgr.removeSubscription(sub);

      // Disconnect the websocket if there is no other event left
      if (subMgr.isEmpty()) {
        close();
      }
    }
  }

  return { subscribe, unsubscribe };
}

type WebsocketManager = ReturnType<typeof createWebsocketManager>;

export type UseWebsocketOptions<T> = {
  criteria?: Criteria;
  event: string;
  onMessage: (message: T) => any;
  skip?: boolean;
};

function createUseWebsocketHook(wsMgr: WebsocketManager) {
  return function <T>({
    criteria = {},
    event,
    onMessage,
    skip = false,
  }: UseWebsocketOptions<T>) {
    const appData = useAppData();
    // Wrap with curly braces
    const userUuid = appData?.user?.uuid ? `{${appData.user.uuid}}` : undefined;

    const [hookId] = useState(uniqueId('useWebsocket_'));
    const onMessageRef = useRef(onMessage);

    useEffect(() => {
      onMessageRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
      if (skip) {
        return () => undefined;
      }

      wsMgr.subscribe(hookId, event, criteria, onMessageRef, userUuid);
      return () => {
        wsMgr.unsubscribe(hookId);
      };

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(criteria), userUuid, event, skip]);
  };
}

// Global singleton instances
const subscriptionManager = createSubscriptionManager();
const websocketManager = createWebsocketManager(subscriptionManager);
export const useWebsocket = createUseWebsocketHook(websocketManager);

/**
 * Constructors, exported for testing purpose.
 * Don't use them in code: only use the `useWebsocket` singleton.
 */
export const privateVariablesOnlyForTests = {
  createSubscriptionManager,
  createUseWebsocketHook,
  createWebsocketManager,
};
